import fetch from '@/assets/js/src/util/fetch'
import { checkResponse, handleException, paramsToQuery, } from '@/assets/js/src/util/apiTools'
import { getDefaultHeaders, } from '@/assets/js/src/util/fetch/defaultHeaders'
import { defineStore, getActivePinia, } from 'pinia'
import { useUserStore, } from '@/assets/js/src/modules/user/_pinia/user'

export const API_GET_ALL_RESOURCES = '/api/resources'

// null for a missing or broken filter, e.g. from a truncated shared link. Missing fields fall back to the defaults,
// other keys of a manipulated link are not passed to the API.
function parseQueryFilter (queryFilter, defaultAbbreviations) {
    if (typeof queryFilter !== 'string') {
        return null
    }

    let filter

    try {
        filter = JSON.parse(queryFilter)
    } catch {
        return null
    }

    if (filter === null || typeof filter !== 'object' || Array.isArray(filter)) {
        return null
    }

    return {
        search: typeof filter.search === 'string' ? filter.search : '',
        abbreviations: Array.isArray(filter.abbreviations) ? filter.abbreviations : defaultAbbreviations,
    }
}

export const useResourceListStore = defineStore('resourceList', {
    state () {
        return {
            // Sort - Params
            reload: false,
            reloading: false,

            // Filter - Params
            search: '',
            books: [],

            // Content - Meta
            responseMeta: {},
            page: 1,
            limit: 96,

            // Content - List
            resourceItems: [],

            // Cache - Options
            mode: 'put',
        }
    },
    getters: {
        count: (state) => state.responseMeta.total,
        maxPages: (state) => state.responseMeta.max_page,
        availableBooks: (state) => state.responseMeta.available_books?.constructor?.name === 'Object' ? state.responseMeta.available_books : {},
    },
    actions: {
        async getResourceItems ({ page, provider, query, }) {
            if (this.reloading) {
                return
            }

            let activePinia = getActivePinia()
            let userStore = useUserStore(activePinia)

            // Everything below in the try block: a reloading flag left at true would block every further load
            this.reloading = true

            try {
                page = page || 1

                let filter = {}
                let limit = this.limit

                switch (provider) {
                case 'dictionary': {
                    const queryFilter = parseQueryFilter(query?.filter, userStore.user.options.resource[provider].abbreviations)

                    // Links like /dictionaries?utm_source=newsletter carry no filter
                    if (!queryFilter) {
                        filter.search = this.search
                        filter.abbreviations = userStore.user.options.resource[provider].abbreviations
                        break
                    }

                    filter = queryFilter
                    await userStore.setUserOptions({
                        option: 'resource/dictionary/abbreviations',
                        data: filter.abbreviations,
                    })
                    this.search = filter.search

                    if (!import.meta.env.SSR) {
                        window.history.replaceState({}, document.title, location.href.split('?')[0])
                    }
                    break
                }
                case 'graphic': {
                    filter.books = this.books
                    limit = 24
                    break
                }
                case 'video': {
                    filter.search = this.search
                    filter.books = this.books
                    filter.abbreviations = userStore.user.options.resource[provider].abbreviations
                    limit = 24
                    break
                }
                }

                let apiResponse = await fetch({
                    url: `${API_GET_ALL_RESOURCES}/${provider}?${paramsToQuery({
                        filter: JSON.stringify(filter),
                        sort: userStore.user.options.resource[provider].sort,
                        page,
                        limit,
                    })}`,
                    options: {
                        headers: getDefaultHeaders({}),
                    },
                })

                if (checkResponse(apiResponse)) {
                    throw new Error('Fehler in Api-Response')
                }

                const { resource_items, ...responseMeta } = apiResponse.data

                this.page = page
                if (page === 1) {
                    this.resourceItems = resource_items
                    this.responseMeta = responseMeta
                } else {
                    this.resourceItems.push(...resource_items)
                }
            } catch (e) {
                handleException(e, true, true)
            } finally {
                this.reloading = false
            }
        },
    },
})
